import { HttpErrorResponse } from '@angular/common/http';
import { ApolloError } from '@apollo/client/core';
import { Order } from '@datorama/akita';
import { TranslateService } from '@ngx-translate/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

import { UserSessionPort } from 'user/port/user-session.port';

import { ApiMutation } from './model/api.mutation';
import { ApiQuery } from './model/api.query';

export enum Direction {
	ASC = 'ASC',
	DESC = 'DESC',
}

export class ApiAdapter {
	constructor(
		protected _userSessionPort: UserSessionPort,
		protected _translateService: TranslateService
	) {}

	public static convertOrderToDirection(order: Order): Direction {
		switch (!!order ? order : Order.DESC) {
			case Order.ASC:
				return Direction.ASC;
			case Order.DESC:
				return Direction.DESC;
			default:
				return Direction.DESC;
		}
	}

	public fetch<R, V>(query: ApiQuery<R, V>, variables?: V): Promise<R> {
		return this.fetch$(query, variables).toPromise();
	}

	public fetch$<R, V>(query: ApiQuery<R, V>, variables?: V): Observable<R> {
		return this._userSessionPort.observeJwToken().pipe(
			take(1),
			switchMap((jwt) =>
				query
					.fetch(variables, {
						context: {
							headers: !!jwt
								? {
										Authorization: jwt,
										'Accept-language': this._translateService.currentLang,
								  }
								: { 'Accept-language': this._translateService.currentLang },
						},
					})
					.pipe(
						map((result) => result.data[query.method] as R),
						catchError((error: ApolloError) => this._handleGraphQLError(error))
					)
			)
		);
	}

	public mutate<R, V>(mutation: ApiMutation<R, V>, variables?: V): Promise<R> {
		return this.mutate$(mutation, variables).toPromise();
	}

	public mutate$<R, V>(
		mutation: ApiMutation<R, V>,
		variables?: V
	): Observable<R> {
		return this._userSessionPort.observeJwToken().pipe(
			take(1),
			switchMap((jwt) =>
				mutation
					.mutate(variables, {
						context: {
							headers: !!jwt
								? {
										Authorization: jwt,
										'Accept-language': this._translateService.currentLang,
								  }
								: { 'Accept-language': this._translateService.currentLang },
						},
					})
					.pipe(
						map((result) => result.data[mutation.method] as R),
						catchError((error: ApolloError) => this._handleGraphQLError(error))
					)
			)
		);
	}

	private _handleGraphQLError(error: ApolloError): Observable<never> {
		if (!!error.networkError) {
			if (error.networkError instanceof HttpErrorResponse) {
				switch (error.networkError.status) {
					case 401:
						this._userSessionPort.closeSession();
						return throwError(new Error('common.error.unauthorized'));
					default:
						return throwError(new Error('common.error.network-request'));
				}
			} else {
				return throwError(new Error('common.error.network-request'));
			}
		} else {
			return throwError(
				new Error(error.graphQLErrors.map((e) => e.message).join('\n'))
			);
		}
	}
}
